import { css } from 'styled-components';
import { TButtonVariants } from '../../components/Global/Atomics/types';
import { handleButtonAnimation } from '../Animations/ButtonAnimations';
import Theme from '../Theme/Theme';
import { handleTypography } from './TypographyStyles';

type TButtonProps = {
    customColor?: keyof typeof Theme.color;
    size?: 'xs' | 's' | 'm' | 'l';
    animateIconLeft?: boolean;
};

type TSizes = 'xs' | 's' | 'm' | 'l';

const applyTypography = (size: TSizes | undefined) => {
    if (size === 'xs') return handleTypography({ typography: 'ui-xs-medium' });
    if (size === 's') return handleTypography({ typography: 'ui-s-medium' });
    if (size === 'm') return handleTypography({ typography: 'ui-m-medium' });
    if (size === 'l') return handleTypography({ typography: 'ui-l-medium' });
};

export const handleButtonVariants = ({ variant, size, animateIconLeft }: TButtonVariants & TButtonProps) => {
    switch (variant) {
        // PRIMARY-BLACK
        case 'primary-black':
            return css`
                color: ${({ theme }) => theme.color['white']};
                background-color: ${({ theme }) => theme.color['black-1']};
                border-radius: 10rem;
                ${() => applyTypography(size)};

                svg {
                    path {
                        fill: ${({ theme }) => theme.color['white']};
                    }
                }

                &:hover {
                    background-color: ${({ theme }) => theme.color['coolgray-12']};
                }

                &:focus,
                &:focus-visible {
                    box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-2']};
                    outline: none;
                }

                &:disabled {
                    color: ${({ theme }) => theme.color['coolgray-11']};
                    background-color: ${({ theme }) => theme.color['coolgray-2']};
                    cursor: default;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['coolgray-11']};
                        }
                    }
                }
            `;

        // PRIMARY-BLUE
        case 'primary-blue':
            return css`
                color: ${({ theme }) => theme.color['white']};
                background-color: ${({ theme }) => theme.color['blue-4']};
                border-radius: 10rem;
                ${() => applyTypography(size)};

                svg {
                    path {
                        fill: ${({ theme }) => theme.color['white']};
                    }
                }

                &:hover {
                    background-color: ${({ theme }) => theme.color['blue-5']};
                }

                &:focus,
                &:focus-visible {
                    box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-1']};
                    outline: none;
                }

                &:disabled {
                    color: ${({ theme }) => theme.color['coolgray-11']};
                    background-color: ${({ theme }) => theme.color['coolgray-2']};
                    cursor: default;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['coolgray-11']};
                        }
                    }
                }
            `;

        // SECONDARY-BLACK
        case 'secondary-black':
            return css`
                color: ${({ theme }) => theme.color['black-1']};
                border-radius: 10rem;
                border: 1.5px solid ${({ theme }) => theme.color['black-1']};
                background-color: transparent;
                ${() => applyTypography(size)};

                svg {
                    path {
                        fill: ${({ theme }) => theme.color['black-1']};
                    }
                }

                &:hover {
                    background-color: ${({ theme }) => theme.color['neutral2']}80;
                }

                &:focus,
                &:focus-visible {
                    box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-2']};
                    outline: none;
                }

                &:disabled {
                    color: ${({ theme }) => theme.color['coolgray-11']};
                    border-color: ${({ theme }) => theme.color['coolgray-2']};
                    cursor: default;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['coolgray-11']};
                        }
                    }
                }
            `;

        // SECONDARY-BLUE
        case 'secondary-blue':
            return css`
                color: ${({ theme }) => theme.color['blue-aa']};
                border-radius: 10rem;
                border: 1.5px solid ${({ theme }) => theme.color['blue-aa']};
                background-color: transparent;
                ${() => applyTypography(size)};

                svg {
                    path {
                        fill: ${({ theme }) => theme.color['blue-aa']};
                    }
                }

                &:hover {
                    background-color: ${({ theme }) => theme.color['neutral2']}80;
                }

                &:focus,
                &:focus-visible {
                    box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-2']};
                    outline: none;
                }

                &:disabled,
                &[disabled] {
                    color: ${({ theme }) => theme.color['coolgray-11']};
                    border-color: ${({ theme }) => theme.color['coolgray-2']};
                    cursor: default;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['coolgray-11']};
                        }
                    }
                }
            `;

        // TEXT-BLUE
        case 'text-blue':
            return css`
                padding: 4px 8px;
                color: ${({ theme }) => theme.color['blue-4']};
                background-color: transparent;
                ${() => applyTypography('m')};
                ${() => handleButtonAnimation({ animateIconLeft })}

                svg {
                    path {
                        fill: ${({ theme }) => theme.color['blue-4']};
                    }
                }

                &:hover {
                    color: ${({ theme }) => theme.color['blue-5']};
                    transform: scale(1) !important;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['blue-5']};
                        }
                    }
                }

                &:focus,
                &:focus-visible {
                    padding: 4px 8px;
                    border-radius: 10rem;
                    box-shadow: 0 0 0 3px ${({ theme }) => theme.color['blue-1']};
                    outline: none;
                }

                &:disabled,
                &[disabled] {
                    color: ${({ theme }) => theme.color['coolgray-7']};
                    cursor: default;

                    svg {
                        path {
                            fill: ${({ theme }) => theme.color['coolgray-11']};
                        }
                    }
                }
            `;

        default:
            break;
    }
};
